import { Alert, Button, Col, Row } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import { api } from '../../../../../auth'
import { FC, useCallback, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { PrivateAPIs, PrivateAPIsFormProps } from '../APIManager.d'
import { useToast } from '../../../../common/toast/hooks/useToast'
import Common from '../../../../common/Common'
import RenderFieldsErrors from '../../../../common/forms/errors/RenderFieldsErrors'
import { Formik, Form as FormikForm } from 'formik'
import { handleAxiosErrors } from '../../../../common/forms/errors/Helper'
import { Card as RBCard } from 'react-bootstrap'
import { v4 as uuidv4 } from 'uuid'
import PrivateAPIsField from './PrivateAPIsField'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch, faPlus } from '@fortawesome/free-solid-svg-icons'

const PrivateAPIsForm: FC<PrivateAPIsFormProps> = ({ config = {} }) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const axiosPrivate = api

    const emptyPrivateAPIs: PrivateAPIs = {
        allowedHosts: [],
    }

    const PrivateAPIsFormSchema = Yup.object().shape({})

    const [privateAPIs, setPrivateAPIs] =
        useState<PrivateAPIs>(emptyPrivateAPIs)
    const [originalPrivateAPIs, setOriginalPrivateAPIs] = useState<Record<string, any>>({})
    const [initialPrivateAPIs, setInitialPrivateAPIs] = useState<Record<string, any>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/administration/private-apis')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                if (response.data.data.private_apis_props) {
                    setInitialPrivateAPIs(response.data.data.private_apis_props)
                    setOriginalPrivateAPIs(
                        response.data.data.private_apis_props
                    )
                    let _privateAPIs: Record<string, any> = {}
                    for (const i in response.data.data.private_apis_props) {
                        const typeValues =
                            response.data.data.private_apis_props[i]
                        for (const x in typeValues) {
                            _privateAPIs[`${i}-${typeValues[x]['id']}`] =
                                typeValues[x]['value']
                        }
                    }
                    setPrivateAPIs(_privateAPIs)
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load hosts')
            }
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        delete record['private_apis_props']

        try {
            const response = await axiosPrivate
                .post('/administration/private-apis/update', record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const getTypeConfig = (type: string) => {
        switch (type) {
            case 'allowedHosts':
                return {
                    type: 'text',
                    label: 'Allowed Host/IP Addresses',
                    singleLabel: 'Host or IP Address',
                }
            default:
                return {}
        }
    }

    const FormFooter = (
        <>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={() => {
                    const formResetBtn = document.getElementById(
                        'privateAPIs-form-reset-btn'
                    )
                    formResetBtn?.click()
                }}
            >
                <span className="small">Reset Hosts</span>
            </Button>
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'privateAPIs-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel ||
                                'Saving Hosts...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Save Hosts'
                    )}
                </span>
            </Button>
        </>
    )

    const FormContent = (
        <>
            <Common.Loader text="Loading..." visible={isLoading} />
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...privateAPIs,
                }}
                validationSchema={PrivateAPIsFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
                onReset={(e, props) => {
                    const { setFieldValue } = props
                    setOriginalPrivateAPIs(initialPrivateAPIs)
                    for (const i in initialPrivateAPIs) {
                        const typeValues: any = initialPrivateAPIs[i]
                        for (const x in typeValues) {
                            setFieldValue(
                                `${i}-${typeValues[x]['id']}`,
                                typeValues[x]['value']
                            )
                        }
                    }
                }}
            >
                {(formikProps) => {
                    return (
                        <FormikForm className="mb-0">
                            <Row className="mb-0">
                                {Object.keys(originalPrivateAPIs).map(
                                    (type: string) => {
                                        const config = getTypeConfig(type)
                                        return (
                                            <Col key={type}>
                                                <RBCard className="shadow mb-3">
                                                    <RBCard.Header
                                                        className={`bg-${
                                                            theme === 'dark'
                                                                ? 'dark'
                                                                : 'light'
                                                        }`}
                                                    >
                                                        <div
                                                            className={`float-start small text-${
                                                                theme === 'dark'
                                                                    ? 'light'
                                                                    : 'dark'
                                                            }`}
                                                        >
                                                            {config.label}
                                                        </div>
                                                        <Button
                                                            variant="outline-primary"
                                                            className="float-end btn-xs"
                                                            onClick={() => {
                                                                setOriginalPrivateAPIs(
                                                                    (
                                                                        prevState
                                                                    ) => {
                                                                        return {
                                                                            ...prevState,
                                                                            [type]: [
                                                                                ...prevState[
                                                                                    type
                                                                                ],
                                                                                {
                                                                                    id:
                                                                                        'new-' +
                                                                                        uuidv4(),
                                                                                    value: '',
                                                                                },
                                                                            ],
                                                                        }
                                                                    }
                                                                )
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faPlus}
                                                                className="me-2"
                                                            />
                                                            New{' '}
                                                            {/* {config.singleLabel} */}
                                                        </Button>
                                                    </RBCard.Header>
                                                    <RBCard.Body>
                                                        <Row>
                                                            <Col xl={12}>
                                                                {originalPrivateAPIs[
                                                                    type
                                                                ].length > 0 ? (
                                                                    originalPrivateAPIs[
                                                                        type
                                                                    ].map(
                                                                        (
                                                                            field: any
                                                                        ) => (
                                                                            <PrivateAPIsField
                                                                                key={
                                                                                    field.id
                                                                                }
                                                                                field={
                                                                                    field
                                                                                }
                                                                                type={
                                                                                    type
                                                                                }
                                                                                config={
                                                                                    config
                                                                                }
                                                                                formikProps={
                                                                                    formikProps
                                                                                }
                                                                                privateAPIs={
                                                                                    privateAPIs
                                                                                }
                                                                                setPrivateAPIs={
                                                                                    setPrivateAPIs
                                                                                }
                                                                                originalPrivateAPIs={
                                                                                    originalPrivateAPIs
                                                                                }
                                                                                setOriginalPrivateAPIs={
                                                                                    setOriginalPrivateAPIs
                                                                                }
                                                                            />
                                                                        )
                                                                    )
                                                                ) : (
                                                                    <h6 className="text-center p-3 small">
                                                                        No{' '}
                                                                        {
                                                                            config.singleLabel
                                                                        }
                                                                        (s)
                                                                        found!
                                                                    </h6>
                                                                )}
                                                            </Col>
                                                        </Row>
                                                    </RBCard.Body>
                                                </RBCard>
                                            </Col>
                                        )
                                    }
                                )}
                                <input
                                    type="submit"
                                    id="privateAPIs-form-submit-btn"
                                    className="d-none"
                                />
                                <input
                                    type="reset"
                                    id="privateAPIs-form-reset-btn"
                                    className="d-none"
                                />
                            </Row>
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )
    return (
        <Row>
            <Col>
                {FormContent}
                <div className="p-dialog border-0 mb-4 mt-0 shadow-none">
                    <div className="p-dialog-footer bg-transparent border-0">
                        {FormFooter}
                    </div>
                </div>
            </Col>
        </Row>
    )
}

export default PrivateAPIsForm
